import React from 'react';
import { Edit, Calendar, Phone, Mail, MapPin, User, FileText, Power, PowerOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { formatRUC } from '../mocks/system-parameters.mock';
import type { TransportCompany } from '../types/system-parameters.types';

interface TransportCompaniesTableProps {
  companies: TransportCompany[];
  onEdit: (company: TransportCompany) => void;
  onDelete: (company: TransportCompany) => void;
}

type SortField = 'companyName' | 'ruc' | 'contactPerson' | 'authorizationExpiry';

const isExpiringSoon = (dateString: string): boolean => {
  const expiryDate = new Date(dateString);
  const today = new Date();
  const diffTime = expiryDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30 && diffDays > 0;
};

export const TransportCompaniesTable: React.FC<TransportCompaniesTableProps> = ({
  companies,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    companyName: 'Empresa',
    ruc: 'RUC',
    contactPerson: 'Contacto',
    authorizationExpiry: 'Autorización MTC'
  };

  const sortValueExtractor = (company: TransportCompany, field: SortField): any => {
    switch (field) {
      case 'companyName':
        return company.additionalData.companyName.toLowerCase();
      case 'ruc':
        return company.additionalData.ruc;
      case 'contactPerson':
        return company.additionalData.contactPerson.toLowerCase();
      case 'authorizationExpiry':
        return company.additionalData.authorizationExpiry ? new Date(company.additionalData.authorizationExpiry) : new Date(0);
      default:
        return company.additionalData.companyName;
    }
  };

  const tableData = useTableData<TransportCompany, SortField>(
    {
      data: companies,
      defaultSortField: 'companyName',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (companies.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresas de transporte</h3>
        <p className="text-gray-500">No se encontraron empresas con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="companyName"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Empresa
              </SortableHeader>
              <SortableHeader
                field="ruc"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                RUC
              </SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dirección
              </th>
              <SortableHeader
                field="contactPerson"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Contacto
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Servicios
              </th>
              <SortableHeader
                field="authorizationExpiry"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Autor. MTC
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((company) => {
              const isAuthorizationExpiring = company.additionalData.authorizationExpiry && isExpiringSoon(company.additionalData.authorizationExpiry);

              return (
                <tr key={company.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {company.additionalData.companyName}
                      </div>
                      <div className="text-xs text-gray-400">
                        ID: {company.id}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-sm font-mono text-gray-900">
                      {formatRUC(company.additionalData.ruc)}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{company.additionalData.address}</span>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-1 text-gray-900">
                        <User className="h-3 w-3 text-gray-500" />
                        {company.additionalData.contactPerson}
                      </div>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Phone className="h-3 w-3 text-gray-500" />
                        {company.additionalData.contactPhone}
                      </div>
                      {company.additionalData.email && (
                        <div className="flex items-center gap-1 text-blue-600">
                          <Mail className="h-3 w-3" />
                          <span className="text-xs">{company.additionalData.email}</span>
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {company.additionalData.serviceType.map((service) => (
                        <Badge key={service} variant="outline" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    {company.additionalData.authorizationExpiry ? (
                      <div className="space-y-1">
                        {company.additionalData.mtcAuthorization && (
                          <div className="flex items-center justify-center gap-1 text-xs text-gray-600">
                            <FileText className="h-3 w-3" />
                            <span className="font-mono">{company.additionalData.mtcAuthorization}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-center gap-2">
                          <Calendar className="h-3 w-3 text-gray-500" />
                          <span className={`text-sm ${isAuthorizationExpiring ? 'text-orange-600 font-semibold' : 'text-gray-900'}`}>
                            {new Date(company.additionalData.authorizationExpiry).toLocaleDateString('es-PE')}
                          </span>
                          {isAuthorizationExpiring && <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">Por vencer</Badge>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>

                  <td className="px-4 py-4 text-center">
                    <Badge className={company.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {company.isActive ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(company)}
                        className="text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(company)}
                        className={`text-xs ${company.additionalData?.isActive
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                      >
                        {company.additionalData?.isActive ? (
                          <><PowerOff className="w-3 h-3 mr-1" />Desactivar</>
                        ) : (
                          <><Power className="w-3 h-3 mr-1" />Reactivar</>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
